import React, { useEffect, useRef } from 'react';

const BOID_COUNT = 900;
const COLORS = ['#8b5cf6', '#6366f1', '#a78bfa', '#c4b5fd', '#4f46e5'];

// Matches original: separation + alignment + cohesion zone thresholds
const SEPARATION = 20;
const ALIGNMENT = 30;
const COHESION = 20;
const ZONE_RADIUS = SEPARATION + ALIGNMENT + COHESION; // 70
const ZONE_RADIUS_SQ = ZONE_RADIUS * ZONE_RADIUS;
const SEP_THRESH = SEPARATION / ZONE_RADIUS;
const ALI_THRESH = (SEPARATION + ALIGNMENT) / ZONE_RADIUS;
const SPEED_LIMIT = 6;
const SPEED_LIMIT_FLEE = 8;
const PI2 = Math.PI * 2;

// Spatial grid for O(n) neighbor lookups instead of O(n²)
class SpatialGrid {
  constructor(cellSize) {
    this.cellSize = cellSize;
    this.cells = new Map();
  }

  clear() {
    this.cells.clear();
  }

  _key(cx, cy) {
    return cx * 73856093 + cy * 19349663; // hash combine
  }

  insert(boid) {
    const cx = Math.floor(boid.x / this.cellSize);
    const cy = Math.floor(boid.y / this.cellSize);
    const key = this._key(cx, cy);
    let cell = this.cells.get(key);
    if (!cell) {
      cell = [];
      this.cells.set(key, cell);
    }
    cell.push(boid);
  }

  getNeighbors(boid) {
    const cx = Math.floor(boid.x / this.cellSize);
    const cy = Math.floor(boid.y / this.cellSize);
    const neighbors = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const cell = this.cells.get(this._key(cx + dx, cy + dy));
        if (cell) {
          for (let i = 0; i < cell.length; i++) {
            neighbors.push(cell[i]);
          }
        }
      }
    }
    return neighbors;
  }
}

// Predator that sweeps through the flock
class Predator {
  constructor(w, h) {
    this.w = w;
    this.h = h;
    this.x = -9999;
    this.y = -9999;
    this.sweepTimer = 0;
    this.nextSweep = 15000 + Math.random() * 10000;
    this.sweeping = false;
    this.targetX = 0;
    this.targetY = 0;
  }

  triggerAt(x, y) {
    this.sweeping = true;
    this.tapped = true;
    this.sweepTimer = 0;
    this.lingerTime = 0.3;
    this.x = x;
    this.y = y;
    const angle = Math.random() * PI2;
    const overshoot = Math.max(this.w, this.h) * 0.5;
    this.targetX = x + Math.cos(angle) * overshoot;
    this.targetY = y + Math.sin(angle) * overshoot;
  }

  update(delta, flockCenterX, flockCenterY) {
    this.sweepTimer += delta;

    if (!this.sweeping && this.sweepTimer > this.nextSweep / 1000) {
      this.sweeping = true;
      this.sweepTimer = 0;
      const angle = Math.random() * PI2;
      const overshoot = Math.max(this.w, this.h) * 0.5;
      this.x = flockCenterX - Math.cos(angle) * overshoot;
      this.y = flockCenterY - Math.sin(angle) * overshoot;
      this.targetX = flockCenterX + Math.cos(angle) * overshoot;
      this.targetY = flockCenterY + Math.sin(angle) * overshoot;
    }

    if (this.sweeping) {
      // Linger at tap point before moving
      if (this.lingerTime > 0) {
        this.lingerTime -= delta;
        return;
      }

      const dx = this.targetX - this.x;
      const dy = this.targetY - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 15) {
        const speed = (this.tapped ? 200 : 350) * delta;
        this.x += (dx / dist) * speed;
        this.y += (dy / dist) * speed;
      } else {
        this.sweeping = false;
        this.tapped = false;
        this.lingerTime = 0;
        this.nextSweep = 15000 + Math.random() * 10000;
        this.x = -9999;
        this.y = -9999;
      }
    }
  }
}

class Boid {
  constructor(w, h) {
    // Spawn in center cluster
    const spawnAngle = Math.random() * PI2;
    const spawnR = Math.random() * 100;
    this.x = w * 0.5 + Math.cos(spawnAngle) * spawnR;
    this.y = h * 0.5 + Math.sin(spawnAngle) * spawnR;
    this.z = Math.random();
    this.vz = 0;
    const angle = Math.random() * PI2;
    const speed = 3 + Math.random() * 2;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed;
    this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
    this.wingPhase = Math.random() * PI2;
    this.wingSpeed = 4 + Math.random() * 3;
    this.noiseOffsetZ = Math.random() * 1000;
  }

  update(neighbors, w, h, delta, predator) {
    let vx = this.vx;
    let vy = this.vy;

    // Predator flee — matches original: strength proportional to distSq/radiusSq
    const pdx = this.x - predator.x;
    const pdy = this.y - predator.y;
    const pDist = Math.sqrt(pdx * pdx + pdy * pdy);
    const preyRadius = predator.tapped ? 150 : 80;
    let fleeing = false;
    if (pDist < preyRadius && pDist > 0) {
      fleeing = true;
      const f = ((pDist * pDist) / (preyRadius * preyRadius)) * delta * 100;
      vx += (pdx / pDist) * f;
      vy += (pdy / pDist) * f;
      this.vz += 0.15 + (Math.random() - 0.3) * 0.2;
    }

    // Gentle center pull
    const cx = w * 0.5;
    const cy = h * 0.5;
    const dcx = this.x - cx;
    const dcy = this.y - cy;
    const dcDist = Math.sqrt(dcx * dcx + dcy * dcy);
    if (dcDist > 0) {
      vx -= (dcx / dcDist) * delta * 4;
      vy -= (dcy / dcDist) * delta * 4;
    }

    // Flocking — single zone with cosine-smoothed bands
    let az = 0, ac = 0;
    for (let i = 0; i < neighbors.length; i++) {
      const other = neighbors[i];
      if (other === this) continue;

      const dx = other.x - this.x;
      const dy = other.y - this.y;
      const distSq = dx * dx + dy * dy;

      if (distSq > ZONE_RADIUS_SQ || distSq < 0.0001) continue;

      // Track neighbor z for depth alignment
      az += other.z;
      ac++;

      const dist = Math.sqrt(distSq);
      const percent = distSq / ZONE_RADIUS_SQ;

      if (percent < SEP_THRESH) {
        // Separation — stronger when closer
        const f = (SEP_THRESH / percent - 1.0) * delta;
        vx -= (dx / dist) * f;
        vy -= (dy / dist) * f;
      } else if (percent < ALI_THRESH) {
        // Alignment — cosine-smoothed
        const threshold = ALI_THRESH - SEP_THRESH;
        const adjustedPercent = (percent - SEP_THRESH) / threshold;
        const f = (0.5 - Math.cos(adjustedPercent * PI2) * 0.5 + 0.5) * delta;
        const otherSpeed = Math.sqrt(other.vx * other.vx + other.vy * other.vy);
        if (otherSpeed > 0) {
          vx += (other.vx / otherSpeed) * f;
          vy += (other.vy / otherSpeed) * f;
        }
      } else {
        // Cohesion — cosine-smoothed attraction
        const threshold = 1.0 - ALI_THRESH;
        const adjustedPercent = (percent - ALI_THRESH) / threshold;
        const f = (0.5 - (Math.cos(adjustedPercent * PI2) * -0.5 + 0.5)) * delta;
        vx += (dx / dist) * f;
        vy += (dy / dist) * f;
      }
    }

    // Speed limit
    const speed = Math.sqrt(vx * vx + vy * vy);
    const limit = fleeing ? SPEED_LIMIT_FLEE : SPEED_LIMIT;
    if (speed > limit) {
      vx = (vx / speed) * limit;
      vy = (vy / speed) * limit;
    }

    this.vx = vx;
    this.vy = vy;
    this.x += vx * delta * 15;
    this.y += vy * delta * 15;

    // Z depth — treat as a real axis with momentum
    // Neighbors pull z together (creates waves of birds rushing at camera in groups)
    if (ac > 0) {
      const avgZ = az / ac;
      this.vz += (avgZ - this.z) * 0.02;
    }
    // Periodic z-surges — some birds occasionally dive toward camera
    const zSurge = Math.sin(performance.now() * 0.0006 + this.noiseOffsetZ);
    if (zSurge > 0.85) {
      this.vz += 0.04; // toward camera
    } else if (zSurge < -0.85) {
      this.vz -= 0.03; // away from camera
    }
    this.vz *= 0.92;
    this.z += this.vz;
    // Allow z to overshoot slightly before clamping for momentum feel
    if (this.z > 1.1) { this.z = 1.1; this.vz *= -0.5; } // bounce back
    if (this.z < -0.1) { this.z = -0.1; this.vz *= -0.5; }
    this.z = Math.max(0, Math.min(1, this.z));

    // Wing flap
    const flapMult = fleeing ? 2.0 : 1.0;
    this.wingPhase += this.wingSpeed * delta * flapMult;
  }

  draw(ctx, w, h) {
    if (this.x < -80 || this.x > w + 80 || this.y < -80 || this.y > h + 80) return;

    const angle = Math.atan2(this.vy, this.vx);
    const depthScale = 0.25 + this.z * 1.0;
    const alpha = 0.12 + this.z * 0.65;

    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.globalAlpha = alpha;

    // Dots (z < 0.6): scale smoothly from miniscule to small
    if (this.z < 0.6) {
      ctx.fillStyle = this.color;
      const r = 0.3 + this.z * 2;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, PI2);
      ctx.fill();
      ctx.restore();
      return;
    }

    // Close (z >= 0.65): bird with flapping wings
    ctx.rotate(angle);
    const s = depthScale;
    const wingFlap = Math.sin(this.wingPhase);
    const wingY = (4 + wingFlap * 3) * s;

    ctx.lineWidth = 1.2 * s;
    ctx.strokeStyle = this.color;
    ctx.beginPath();
    ctx.moveTo(3 * s, 0);
    ctx.lineTo(-2 * s, 0);
    ctx.stroke();

    const wingX = 1 * s;
    ctx.lineWidth = 1 * s;
    ctx.beginPath();
    ctx.moveTo(wingX, 0);
    ctx.quadraticCurveTo(wingX - 1.5 * s, -wingY * 0.6, wingX - 3 * s, -wingY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(wingX, 0);
    ctx.quadraticCurveTo(wingX - 1.5 * s, wingY * 0.6, wingX - 3 * s, wingY);
    ctx.stroke();

    ctx.restore();
  }
}

const Murmuration = ({ width, height }) => {
  const canvasRef = useRef(null);
  const boidsRef = useRef([]);
  const predatorRef = useRef(null);
  const rafRef = useRef(null);
  const isRunning = useRef(true);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = width || 500;
    const h = height || 500;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);

    boidsRef.current = Array.from({ length: BOID_COUNT }, () => new Boid(w, h));
    predatorRef.current = new Predator(w, h);
    lastTimeRef.current = performance.now();
    const grid = new SpatialGrid(ZONE_RADIUS);

    isRunning.current = true;

    const animate = (now) => {
      if (!isRunning.current) return;

      let delta = (now - lastTimeRef.current) / 1000;
      if (delta > 1) delta = 1;
      lastTimeRef.current = now;

      ctx.clearRect(0, 0, w, h);

      const boids = boidsRef.current;
      const predator = predatorRef.current;

      // Build spatial grid for O(n) neighbor lookups
      grid.clear();
      let fcx = 0, fcy = 0;
      for (let i = 0; i < boids.length; i++) {
        grid.insert(boids[i]);
        fcx += boids[i].x;
        fcy += boids[i].y;
      }
      fcx /= boids.length;
      fcy /= boids.length;

      predator.update(delta, fcx, fcy);

      // Depth sort
      boids.sort((a, b) => a.z - b.z);

      for (let i = 0; i < boids.length; i++) {
        const neighbors = grid.getNeighbors(boids[i]);
        boids[i].update(neighbors, w, h, delta, predator);
        boids[i].draw(ctx, w, h);
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      isRunning.current = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [width, height]);

  // Pause/resume with IntersectionObserver
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!isRunning.current) {
            isRunning.current = true;
            lastTimeRef.current = performance.now();
            const dpr = window.devicePixelRatio || 1;
            const w = canvas.width / dpr;
            const h = canvas.height / dpr;
            const ctx = canvas.getContext('2d');

            const resumeGrid = new SpatialGrid(ZONE_RADIUS);
            const resume = (now) => {
              if (!isRunning.current) return;
              let delta = (now - lastTimeRef.current) / 1000;
              if (delta > 1) delta = 1;
              lastTimeRef.current = now;

              ctx.save();
              ctx.setTransform(1, 0, 0, 1, 0, 0);
              ctx.scale(dpr, dpr);
              ctx.clearRect(0, 0, w, h);

              const boids = boidsRef.current;
              const predator = predatorRef.current;
              resumeGrid.clear();
              let fcx = 0, fcy = 0;
              for (let i = 0; i < boids.length; i++) {
                resumeGrid.insert(boids[i]);
                fcx += boids[i].x;
                fcy += boids[i].y;
              }
              fcx /= boids.length; fcy /= boids.length;
              predator.update(delta, fcx, fcy);
              boids.sort((a, b) => a.z - b.z);
              for (let i = 0; i < boids.length; i++) {
                const neighbors = resumeGrid.getNeighbors(boids[i]);
                boids[i].update(neighbors, w, h, delta, predator);
                boids[i].draw(ctx, w, h);
              }
              ctx.restore();
              rafRef.current = requestAnimationFrame(resume);
            };
            rafRef.current = requestAnimationFrame(resume);
          }
        } else {
          isRunning.current = false;
          cancelAnimationFrame(rafRef.current);
        }
      },
      { threshold: 0 }
    );

    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const handleTap = (e) => {
    if (!predatorRef.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    predatorRef.current.triggerAt(x, y);
  };

  return (
    <canvas
      ref={canvasRef}
      onClick={handleTap}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
      }}
    />
  );
};

export default Murmuration;
